import { Dialog, DialogContent, Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BGImage from "assets/wizardCheckList.png";
import { postUserVisit, setLoadPages } from "components/app/App.slice";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getHltInncodes } from "utils/CommonFunctions";
import { ON_BOARDING } from "utils/URLConstants";
import { commonApiCallParam, updatePopupHeader } from "utils/UserAnalyticsUtils";
import { fetchOnboardingQueries, reset, setRetakeWizardRequired, setShowOnboardingWizard } from "./Onboarding.slice";
import StepContent from "./StepContent";
import WelcomeWizard from "./WelcomeWizard";

const flow: any = {
  "1": "Onboarding Wizard - Step1",
  "2": "Onboarding Wizard - Step2",
  "3": "Onboarding Wizard - Step3"
};

function Index() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const onboardingDetails: any = useAppSelector((state) => state.Onboarding.Onboarding);
  const { isNewFocusAreaAvailable, retakeWizardRequired } = useAppSelector(
    (state) => state.Onboarding.checkApiResponse
  );
  const isRetakeWizard: any = useAppSelector((state) => state.Onboarding.isRetakeWizard);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isAboveProperty = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);
  const isAbovePropertyUserFetched: any = useAppSelector(
    (state) => state.app.loggedInUserDetails.isAbovePropertyUserFetched
  );
  const isAbovePropertyUser = isAbovePropertyUserFetched ? isAboveProperty : true;
  const isOnboardingCompleted: any = useAppSelector((state) => state.Onboarding.isOnboardingCompleted);
  const showOnboardingWizard: any = useAppSelector((state) => state.Onboarding.showOnboardingWizard);
  const isOnboardingCheckApiCompleted = useAppSelector((state) => state.Onboarding.isOnboardingCheckApiCompleted);
  const showOnboardingWizardFromLibrary: any = useAppSelector(
    (state) => state.Onboarding.showOnboardingWizardFromLibrary
  );
  const [steps, setSteps] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      dispatch(reset());
      dispatch(setShowOnboardingWizard(false));
    };
  }, []);

  useEffect(() => {
    if (!isCorp) {
      if (!isOnboardingCompleted && isEmpty(onboardingDetails) && !isImpersonated) {
        dispatch(
          fetchOnboardingQueries({
            wizardType: ON_BOARDING,
            payload: {
              innCode: getHltInncodes(true)
            }
          })
        );
      }

      const showWizard = isImpersonated
        ? showOnboardingWizardFromLibrary
        : !isOnboardingCompleted || showOnboardingWizardFromLibrary;

      if (showWizard && !isEmpty(onboardingDetails)) {
        dispatch(setShowOnboardingWizard(true));
        dispatch(setLoadPages(false));
      } else {
        dispatch(setShowOnboardingWizard(false));
        dispatch(setLoadPages(true));
      }
    } else {
      dispatch(setShowOnboardingWizard(false));
      dispatch(setLoadPages(true));
    }
  }, [
    isCorp,
    isAbovePropertyUser,
    onboardingDetails,
    isOnboardingCompleted,
    isImpersonated,
    showOnboardingWizardFromLibrary
  ]);

  useEffect(() => {
    if (showOnboardingWizardFromLibrary || isImpersonated) {
      setCurrentPage(2);
    } else {
      setCurrentPage(1);
    }
  }, [showOnboardingWizardFromLibrary, isImpersonated]);

  useEffect(() => {
    let params = commonApiCallParam(true);
    let updatedparam = "";
    if (isOnboardingCheckApiCompleted && showOnboardingWizard) {
      if (isRetakeWizard) {
        if (steps === 1) updatedparam = updatePopupHeader("Retake Wizard");
      } else {
        updatedparam = updatePopupHeader(flow[steps] ? `${flow[steps]}` : "Onboarding Wizard");
      }
      params.popupHeader = updatedparam;
      if (updatedparam) dispatch(postUserVisit(params));
    }
  }, [steps, showOnboardingWizard]);

  const handleClose = () => {
    dispatch(reset());
    dispatch(setRetakeWizardRequired(false));
  };

  return (
    <>
      {showOnboardingWizard && (
        <Dialog
          fullScreen={matches}
          maxWidth={"md"}
          open={true}
          onClose={(e, reason) => {
            if (reason && reason === "backdropClick") return;
            handleClose();
          }}
        >
          <DialogContent sx={{ display: "flex", padding: 0 }}>
            <Grid container>
              <Grid
                item
                md={5}
                xs={0}
                sx={{
                  margin: 0,
                  backgroundPositionX: "40%",
                  backgroundPositionY: "calc(100% - 100px)",
                  backgroundImage: `url(${BGImage})`,
                  backgroundColor: tokens.Border.General.Color,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "50%",
                  color: tokens.Text.General.Inverse,
                  width: "400px",
                  display: {
                    xs: "none",
                    sm: "none",
                    md: "inherit",
                    minHeight: 450,
                    justifyContent: "center"
                  }
                }}
              >
                <Typography sx={{ padding: "50px 60px" }} variant={"h5"} showBold>
                  {currentPage === 1
                    ? `Hilton BOOST`
                    : `
                  ${isNewFocusAreaAvailable ? translate("onboarding.new.focusAreas") : ""} 
                 ${
                   retakeWizardRequired || showOnboardingWizardFromLibrary
                     ? translate("onboarding.preferences")
                     : translate("onboarding.setPreferences")
                 }
                  
                 `}
                </Typography>
              </Grid>
              <Grid
                container
                sx={{ padding: "16px", minHeight: 500 }}
                width="100%"
                item
                md={7}
                display={"flex"}
                flexDirection="column"
                alignItems="center"
                justifyContent={"space-around"}
              >
                {currentPage === 1 ? (
                  <WelcomeWizard setCurrentPage={setCurrentPage} />
                ) : (
                  <StepContent onboardingDetails={onboardingDetails} onStepChange={(step) => setSteps(step)} />
                )}
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default Index;
